import useTranslations from "hooks/useTranslations"
import RequestButton from "components/molecules/RequestButton"
import ImageUsAll from "components/ImageUsAll"
import {
  LifebuoyIcon,
  MapIcon,
  UserGroupIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline"
import Stats from "components/organisms/common/Stats"

export default function ValueProposition() {
  const {
    valuePropositionConceptualTitle,
    valuePropositionConceptualText,
    valuePropositionConceptual1,
    valuePropositionConceptual2,
    valuePropositionConceptual3,
    valuePropositionConceptual4,
    valuePropositionFoundationTitle,
    valuePropositionFoundationText1,
    valuePropositionFoundationText2,
    valuePropositionFoundationLinkFinland,
    valuePropositionFoundationLinkSweden,
    valuePropositionPartnersTitle,
    valuePropositionPartnersText1,
    valuePropositionPartnersText2,
    valuePropositionPartnersText3,
    companyFact1Large,
    companyFact1Small,
    companyFact2Large,
    companyFact2Small,
    companyFact5Large,
    companyFact5Small,
  } = useTranslations()

  const stats = [
    {
      line1: companyFact1Large,
      line2: companyFact1Small,
    },
    {
      line1: companyFact5Large,
      line2: companyFact5Small,
    },
    {
      line1: companyFact2Large,
      line2: companyFact2Small,
    },
  ]

  return (
    <div className="relative bg-white">
      <div className="mx-auto max-w-2xl lg:max-w-7xl ">
        {/* Conceptual Solution Section */}
        <div className="my-20 space-y-8  px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            <h2 className="text-3xl font-extrabold tracking-tight text-zinc-900 md:text-4xl lg:text-5xl">
              {valuePropositionConceptualTitle}
            </h2>
            <p className="max-w-4xl text-lg text-zinc-700">
              {valuePropositionConceptualText}
            </p>
          </div>
          <ul className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <ValueItem icon={<LifebuoyIcon />}>
              {valuePropositionConceptual1}
            </ValueItem>
            <ValueItem icon={<MapIcon />}>
              {valuePropositionConceptual2}
            </ValueItem>
            <ValueItem icon={<UserGroupIcon />}>
              {valuePropositionConceptual3}
            </ValueItem>
            <ValueItem icon={<RocketLaunchIcon />}>
              {valuePropositionConceptual4}
            </ValueItem>
          </ul>
        </div>

        <Stats facts={stats} />

        {/* Our Foundation Section */}
        <div className="my-20 lg:mx-auto  lg:grid lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-2 lg:gap-24 lg:px-8">
          <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:col-start-2 lg:mx-0 lg:max-w-none lg:px-0 ">
            <h2 className="text-5xl font-extrabold tracking-tight text-zinc-900">
              {valuePropositionFoundationTitle}
            </h2>
            <div className="space-y-4">
              <p className="mb-0 text-lg text-zinc-800">
                {valuePropositionFoundationText1}
              </p>
              <p className="mb-0 text-lg text-zinc-800">
                {valuePropositionFoundationText2}
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://www.someturva.fi/us"
                  className="block text-base font-medium text-teal-700 hover:text-teal-600"
                >
                  {valuePropositionFoundationLinkFinland}
                  <span aria-hidden="true"> &rarr;</span>
                </a>
                <a
                  href="https://www.somebuddy.se/us"
                  className="text-base font-medium text-teal-700 hover:text-teal-600"
                >
                  {valuePropositionFoundationLinkSweden}
                  <span aria-hidden="true"> &rarr;</span>
                </a>
              </div>
            </div>
          </div>
          <div className="relative mx-auto mt-12 max-w-2xl px-4 sm:mt-16 sm:px-6 lg:col-start-1 lg:mx-0 lg:mt-0 lg:max-w-none lg:px-0">
            <div className="flex items-center lg:absolute lg:inset-0 lg:inset-y-10 lg:m-0 lg:px-0">
              <ImageUsAll className="h-full w-full rounded-xl border border-white lg:rounded-r-full" />
            </div>
          </div>
        </div>

        {/* For Partners Section */}
        <div className="my-20 px-4 sm:px-8 lg:px-20">
          <div className="space-y-8 rounded-2xl bg-kkm-lightgreen p-10">
            <div className="space-y-4">
              <h2 className="text-3xl font-extrabold tracking-tight text-zinc-900 md:text-4xl lg:text-5xl">
                {valuePropositionPartnersTitle}
              </h2>
            </div>
            <ul className="grid gap-6 lg:grid-cols-3">
              <PatnersItem text={valuePropositionPartnersText1} />
              <PatnersItem text={valuePropositionPartnersText2} />
              <PatnersItem text={valuePropositionPartnersText3} />
            </ul>
            <div className="pt-4">
              <RequestButton oversized bgStyle="light" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
const ValueItem = ({ children, icon }) => {
  return (
    <li className=" flex flex-col justify-between gap-3 rounded-2xl bg-gradient-to-b from-white to-kkm-lightgreen/50 p-8 xl:aspect-square ">
      <span className="mt-1 inline-block h-12 w-12 text-kkm-green">{icon}</span>
      <span className="font-display text-xl text-sb-darkGreen lg:text-lg">
        {children}
      </span>
    </li>
  )
}

const PatnersItem = ({ text }) => {
  return (
    <li className="space-y-2 rounded-lg border-l-4 border-kkm-darkgreen bg-white/50 px-4 py-2 lg:px-6 lg:py-4">
      <div className="font-display text-lg font-bold text-zinc-900">
        {text.split(":")[0]}
      </div>
      <p className="text-base text-zinc-700">{text.split(": ")[1]}</p>
    </li>
  )
}
